// sx-film-banner: the poster paints first. On screen, a silent loop plays (lighter rendition) with a 44 px pause toggle;
// never under reduced motion or Save-Data, and a refused autoplay leaves the poster with the toggle showing "play".
// "Watch" restarts the full film (up to 1080) with sound, fades any text on the film and shows a sound toggle and a
// progress line; at the end the silent loop (or the poster) returns. Off screen or in a hidden tab the film pauses.
// The title rises once, line by line, when it is 60 % in view (at once when it can never be, or was scrolled past).
// Block scope: the class name stays local, so a second banner on a page (a second copy of this script) cannot throw.
if (!customElements.get('sx-film-banner')) {
class SxFilmBanner extends HTMLElement {
  connectedCallback() {
    if (this.ready) { this.reattach(); return; }
    this.ready = true;
    this.video = this.querySelector('video');
    this.watchButton = this.querySelector('[data-sx-film-watch]');
    this.controls = this.querySelector('[data-sx-film-controls]');
    this.toggle = this.querySelector('[data-sx-film-toggle]');
    this.sound = this.querySelector('[data-sx-film-sound]');
    this.bar = this.querySelector('[data-sx-film-bar]');
    this.section = this.closest('.sx-film');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.section && !reduce && 'IntersectionObserver' in window) this.armReveal();
    if (!this.video || !this.controls || !this.toggle || !this.sound) return;
    this.phone = matchMedia('(max-width: 749px)').matches;
    this.mode = 'idle';
    this.userPaused = false;
    this.inView = false;
    const saveData = navigator.connection && navigator.connection.saveData === true;
    this.loopAllowed = this.dataset.autoplay === 'true' && !reduce && !saveData;
    if (this.watchButton) this.watchButton.addEventListener('click', () => this.watch());
    this.toggle.addEventListener('click', () => this.togglePlay());
    this.sound.addEventListener('click', () => this.setMuted(!this.video.muted));
    this.video.addEventListener('click', () => { if (this.mode !== 'idle') this.togglePlay(); });
    this.video.addEventListener('play', () => {
      this.toggle.setAttribute('aria-pressed', 'false');
      if (this.mode === 'watch') this.tick();
    });
    this.video.addEventListener('playing', () => this.classList.add('is-revealed'));
    this.video.addEventListener('pause', () => this.toggle.setAttribute('aria-pressed', 'true'));
    this.video.addEventListener('ended', () => this.endWatch());
    this.video.addEventListener('error', () => this.fail());
    this.io = new IntersectionObserver((entries) => {
      this.inView = entries[entries.length - 1].isIntersecting;
      this.sync();
    }, { threshold: 0.1 });
    this.io.observe(this);
    this.onVisibility = () => this.sync();
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.revealIO) this.revealIO.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    cancelAnimationFrame(this.raf);
    if (this.video && !this.video.paused) this.video.pause();
  }

  reattach() {
    // A move in the DOM (a section reorder in the theme editor) reconnects the element: watch the view and the title again.
    if (this.revealIO && !this.section.classList.contains('is-in')) this.revealIO.observe(this.revealTarget);
    if (this.failed || !this.io) return;
    this.io.observe(this);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  armReveal() {
    this.revealTarget = this.section.querySelector('.sx-film__title') || this;
    this.section.classList.add('is-armed');
    this.revealIO = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const tall = entry.boundingClientRect.height > innerHeight * 0.6;
      const passed = entry.boundingClientRect.bottom < 0;
      if (entry.intersectionRatio >= 0.6 || (tall && entry.isIntersecting) || passed) {
        this.section.classList.add('is-in');
        this.revealIO.disconnect();
      }
    }, { threshold: [0, 0.6] });
    this.revealIO.observe(this.revealTarget);
  }

  source(kind) {
    const mobile = this.dataset[`${kind}Mobile`];
    const desktop = this.dataset[`${kind}Desktop`];
    return this.phone ? mobile || desktop : desktop;
  }

  sync() {
    if (this.failed) return;
    const visible = this.inView && document.visibilityState === 'visible';
    if (this.mode === 'idle') {
      if (visible && this.loopAllowed && !this.userPaused) this.loop();
      return;
    }
    if (!visible) {
      if (!this.video.paused) this.video.pause();
    } else if (this.mode === 'loop' && !this.userPaused && this.video.paused) {
      this.play();
    }
  }

  load(src) {
    if (this.video.getAttribute('src') === src) return false;
    this.classList.remove('is-revealed');
    this.video.src = src;
    return true;
  }

  loop() {
    const src = this.source('loop');
    if (!src) return this.fail();
    this.mode = 'loop';
    this.classList.remove('is-watching');
    this.load(src);
    this.video.loop = true;
    this.setMuted(true);
    this.sound.hidden = true;
    this.controls.hidden = false;
    if (this.watchButton) this.watchButton.hidden = false;
    if (!this.userPaused && this.inView) this.play();
  }

  watch() {
    const src = this.source('film') || this.source('loop');
    if (!src) return this.fail();
    this.mode = 'watch';
    this.userPaused = false;
    this.classList.add('is-watching');
    if (!this.load(src)) {
      try { this.video.currentTime = 0; } catch (error) { /* not seekable yet */ }
    }
    this.video.loop = false;
    this.setMuted(false);
    this.sound.hidden = false;
    this.controls.hidden = false;
    if (this.watchButton) this.watchButton.hidden = true;
    this.toggle.focus({ preventScroll: true });
    this.play();
    if (!this.video.paused) this.toggle.setAttribute('aria-pressed', 'false');
    this.tick();
  }

  endWatch() {
    if (this.mode !== 'watch') return;
    const hadFocus = this.controls.contains(document.activeElement);
    cancelAnimationFrame(this.raf);
    if (this.bar) this.bar.style.transform = 'scaleX(0)';
    if (this.loopAllowed) {
      this.userPaused = false;
      this.loop();
    } else {
      this.mode = 'idle';
      this.classList.remove('is-watching', 'is-revealed');
      this.controls.hidden = true;
      if (this.watchButton) this.watchButton.hidden = false;
    }
    if (hadFocus && this.watchButton) this.watchButton.focus({ preventScroll: true });
  }

  togglePlay() {
    if (this.video.paused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.video.pause();
    }
  }

  play() {
    const attempt = this.video.play();
    if (!attempt) return;
    attempt.catch((error) => {
      if (error.name === 'NotAllowedError') {
        if (this.mode === 'watch' && !this.video.muted) {
          this.setMuted(true);
          this.video.play().catch(() => {});
        } else {
          this.userPaused = true;
          this.toggle.setAttribute('aria-pressed', 'true');
        }
      } else if (error.name === 'NotSupportedError') {
        this.fail();
      }
    });
  }

  setMuted(muted) {
    this.video.muted = muted;
    this.sound.setAttribute('aria-pressed', String(muted));
  }

  tick() {
    cancelAnimationFrame(this.raf);
    const step = () => {
      const duration = this.video.duration;
      if (duration > 0 && this.bar) this.bar.style.transform = `scaleX(${Math.min(1, this.video.currentTime / duration)})`;
      if (this.mode === 'watch' && !this.video.paused && !this.video.ended) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  fail() {
    // Terminal: no retry when the tab returns; focus leaves the hidden controls.
    this.failed = true;
    if (this.io) this.io.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    cancelAnimationFrame(this.raf);
    if (this.contains(document.activeElement)) document.activeElement.blur();
    this.mode = 'idle';
    this.classList.remove('is-revealed', 'is-watching');
    this.controls.hidden = true;
    if (this.watchButton) this.watchButton.hidden = true;
  }
}
customElements.define('sx-film-banner', SxFilmBanner);
}
